import { Component, ComponentClass, createElement } from 'react';

/**
 * In-repo replacement for react-onclickoutside.
 *
 * That package is a React 19 blocker and cannot stop being one: the top of its ESM bundle
 * is `import { findDOMNode } from 'react-dom'`, and React 19 does not export it, so webpack
 * fails to resolve the specifier whether or not the line ever runs. 6.13.2 is the last
 * release and its peer range stops at React 18.
 *
 * Replacing it is cheap only because of the sweep that came before: every one of the
 * eighteen wrapped components already exposes `setClickOutsideRef()` returning its own root
 * node, which was added to stop the package reaching for findDOMNode at runtime. That
 * method is now the only way this HOC resolves a node — there is no fallback, because a
 * component that forgets it should fail loudly rather than silently stop closing.
 *
 * The behaviour kept is the behaviour the application relies on, and no more:
 *
 * - listens on `document` for `mousedown` and `touchstart`, the package's defaults;
 * - ignores a click inside the component, or inside anything carrying
 *   `outsideClickIgnoreClass` (InfoCard passes `withTooltip`);
 * - dispatches to the `handleClickOutside` prop first and the instance method second,
 *   which is the order the package used and which CustomCalendar depends on;
 * - ignores events older than the mount, so the click that opens a menu cannot also
 *   close it.
 *
 * Dropped, because nothing here used them: `eventTypes`, `excludeScrollbar`,
 * `preventDefault`, `stopPropagation`, the injected `enableOnClickOutside` and
 * `disableOnClickOutside` props, `getClass` and `getInstance`.
 */

const eventTypes: Array<'mousedown' | 'touchstart'> = ['mousedown', 'touchstart'];

/** The package's default, kept so any stray markup using it keeps working. */
export const IGNORE_CLASS_NAME = 'ignore-react-onclickoutside';

export interface ClickOutsideProps {
  /** Clicks inside an element with this class count as inside the component. */
  outsideClickIgnoreClass?: string;
}

/**
 * `handleClickOutside` is deliberately absent from ClickOutsideProps and typed loosely
 * here. Components that take it as a prop declare it themselves, with their own event
 * type — CustomCalendar says React.MouseEvent — and adding it to the HOC's props would
 * intersect the two into a signature nothing can satisfy. The package's types left it out
 * for the same reason.
 */
interface ClickOutsideInstance {
  props: { handleClickOutside?(event: Event): void };
  setClickOutsideRef?(): Element | null;
  handleClickOutside?(event: Event): void;
}

/** Walks up from the click target looking for the component, or for the ignore class. */
function isInside(target: Node | null, node: Element, ignoreClass: string): boolean {
  let current: Node | null = target;

  while (current) {
    if (current === node) {
      return true;
    }
    if (current instanceof Element && current.classList.contains(ignoreClass)) {
      return true;
    }
    current = current.parentNode || (current as Element & { host?: Node }).host || null;
  }

  return false;
}

export function onClickOutside<P extends object>(
  Wrapped: ComponentClass<P>
): ComponentClass<P & ClickOutsideProps> {
  const componentName = Wrapped.displayName || Wrapped.name || 'Component';

  return class OnClickOutside extends Component<P & ClickOutsideProps> {
    public static displayName = `OnClickOutside(${componentName})`;

    private instance: ClickOutsideInstance | null = null;

    /**
     * The event that mounted this component is still being dispatched when the listener
     * goes on, and its target is by definition outside. Without this guard, the click that
     * opens a menu closes it again in the same tick.
     *
     * This is the package's mechanism unchanged, and it has no test: jsdom stamps events
     * with Date.now() while browsers measure event.timeStamp from the performance time
     * origin, so under jest the comparison is never true and the guard never fires. It is
     * kept because the browser behaviour is what the menus depend on.
     */
    private mountedAt = 0;

    private setInstance = (instance: ClickOutsideInstance | null) => {
      this.instance = instance;
    }

    private handleEvent = (event: Event) => {
      const instance = this.instance;

      if (!instance || event.timeStamp < this.mountedAt) {
        return;
      }

      if (typeof instance.setClickOutsideRef !== 'function') {
        throw new Error(
          `${componentName} must expose setClickOutsideRef() returning its root element.`
        );
      }

      const node = instance.setClickOutsideRef();

      if (!node) {
        // A null node here is always a bug: the ref setClickOutsideRef names is not the one
        // that reached the DOM, so outside clicks are ignored and nothing else goes wrong
        // loudly enough to notice. MyAccountWindow shipped in that state for two phases.
        if (process.env.NODE_ENV !== 'production') {
          console.error(
            `${componentName}: setClickOutsideRef() returned no node, so clicks outside it ` +
            'are ignored. It must name the ref that render() attaches to the DOM.'
          );
        }

        return;
      }

      const ignoreClass = this.props.outsideClickIgnoreClass || IGNORE_CLASS_NAME;

      if (isInside(event.target as Node | null, node, ignoreClass)) {
        return;
      }

      if (typeof instance.props.handleClickOutside === 'function') {
        instance.props.handleClickOutside(event);

        return;
      }

      if (typeof instance.handleClickOutside === 'function') {
        instance.handleClickOutside(event);
      }
    }

    public componentDidMount() {
      // performance.now(), not Date.now(): event.timeStamp is measured from the same
      // origin, and comparing it against epoch milliseconds would discard every event.
      this.mountedAt = performance.now();
      eventTypes.forEach(eventType =>
        document.addEventListener(eventType, this.handleEvent)
      );
    }

    public componentWillUnmount() {
      eventTypes.forEach(eventType =>
        document.removeEventListener(eventType, this.handleEvent)
      );
    }

    public render() {
      return createElement(Wrapped, { ...this.props as P, ref: this.setInstance });
    }
  };
}
